import {RunConfigService, UrlService} from '@nuskin/ns-util';
import {UserService} from '@nuskin/ns-account';
import {ServiceUtils} from '@nuskin/ns-shop';
import axios from 'axios';

let baseUrl;
let awsUrl;
const SHIP_DAYS_ROOT_PATH = '/shop-service/api/v2/ship-days';
const AWS_SHIPPING_PATH = '/shippingprofile/v1/';
const USER = UserService.getUser();
const runConfig = RunConfigService.getRunConfig();
let eid = '';
let id = '';

const ZZIP_URLS = {
    dev: 'https://apis.dev.nuskin.com/zzip/v2/:country/:code',
    test: 'https://apis.test.nuskin.com/zzip/v2/:country/:code',
    prod: 'https://apis.nuskin.com/zzip/v2/:country/:code'
};
const STREET_ADDRESS_VALIDATOR_URLS = {
    dev: 'https://apis.dev.nuskin.com/street-address/validate/v1/',
    test: 'https://apis.test.nuskin.com/street-address/validate/v1/',
    prod: 'https://apis.nuskin.com/street-address/validate/v1/'
};

const zzipCache = {}

/**
 *  Holds market's adr blackout period, blackout days, and default ship days
 *  @type Promise<{
 *    "status": 200,
 *    "messages": [],
 *    "responseClass": "com.nuskin.services.shop.model.ShipDaysResponse",
 *    "ShipDaysResponse": {
 *      "blackoutDays": [1,2,3,29,30,31],
 *      "defaultShipDay": 26,
 *      "blackoutPeriod": 10
 *    }
 *  }>
 */
let shipDays;

// Note: make sure to call the getHeaders in each asynchronous function below so that this gets set and doesn't get
// called again for efficiency purposes. Do not call getHeaders here. Found an odd runtime issue.
let headers = null;

if (USER) {
    eid = USER.eid;
    id = USER.id;
}

export default {
    getShippingAddressList,
    postShippingAddress,
    putShippingAddress,
    deleteShippingAddress,
    getShipDays,
    getVNRegionList,
    getListOfCitiesFromPostalCode,
    useAddressValidationService
};

/**
 * GET for Shipping Address endpoint
 * @param {*} includesBe
 * @param {string} [countryCode]
 * @returns {Promise<ShippingAddressListResponse>}
 */
async function getShippingAddressList(countryCode = runConfig.country) {
    let url = getUrl({eid, countryCode, locale: `${runConfig.language}_${runConfig.country}`}, id);

    let response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(eid)
    });
    return ServiceUtils.transformFetchResponse(response);
}

/**
 * POST for Shipping Address endpoint
 * @param {ShippingAddressDTO} body
 * @returns {Promise<ShippingAddressResponse>}
 */
async function postShippingAddress (body) {
    let url = getUrl({eid, locale: `${runConfig.language}_${runConfig.country}`}, id);

    let response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(eid),
        credentials: 'omit',
        body: JSON.stringify(body)
    });
    return ServiceUtils.transformFetchResponse(response);
}

/**
 * PUT for Shipping Address endpoint
 * @param {ShippingAddressDTO} body
 * @returns {Promise<ShippingAddressResponse>}
 */
async function putShippingAddress (body) {
    let url = getUrl({eid, locale: `${runConfig.language}_${runConfig.country}`}, id);

    let response = await fetch(url, {
        method: 'PUT',
        headers: getHeaders(eid),
        credentials: 'omit',
        body: JSON.stringify(body)
    });
    return ServiceUtils.transformFetchResponse(response);
}

/**
 * DELETE for Shipping Address endpoint.
 * Note that it takes a shipping address ID instead of a body
 * @param {*} beShippingAddressId
 */
async function deleteShippingAddress (beShippingAddressId) {
    let url = getUrl({eid, locale: `${runConfig.language}_${runConfig.country}`}, beShippingAddressId);

    let response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(eid),
        credentials: 'omit'
    });
    return ServiceUtils.transformFetchResponse(response);
}

/**
 * Retrieves the Ship Days from the server for the given ADR ID
 * @returns {Promise<ShipDaysResponse>}
 */
async function getShipDays () {
    if (shipDays) return shipDays;
    if (!baseUrl) { baseUrl = UrlService.getSiteUrl(); }
    let url = `${baseUrl}${SHIP_DAYS_ROOT_PATH}/${runConfig.country}?locale=${runConfig.language}_${runConfig.country}`;
    if (!headers) headers = ServiceUtils.getHeaders();
    let response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'omit'
    });
    shipDays = ServiceUtils.transformFetchResponse(response);
    return shipDays;
}

/**
 * Retrieves region, district, and city data for Vietnam.
 * @return {Promise<VNRegion[]>}
 */
async function getVNRegionList () {
    if (!baseUrl) {
        baseUrl = RunConfigService.getSiteUrl();
    }
    const url = baseUrl + '/static/address-form-assets/vn-regions.json';

    const { data } = await axios.get(url, {
        headers: { Accept: 'application/json' }
    });

    return data || [];
}

/**
 * Retrieves address data matching the given postal code and country. Postal code may
 * resolve down to a region, district, or city.
 * @param {string} country - Country code
 * @param {string} zip - Postal code
 * @return {Promise<PostalCodeLookupDTO[]>} Promise that resolves to address data set
 */
async function getListOfCitiesFromPostalCode (country, zip) {
    const cacheKey = `${country}--${zip}`
    // init cache with promise
    if (!zzipCache[cacheKey]) {
        const url = ZZIP_URLS[RunConfigService.getEnvironmentCode()]
            .replace(':country', country)
            .replace(':code', zip);

        zzipCache[cacheKey] = fetch(url, {
            method: 'GET',
            credentials: 'omit'
        })
            .then(ServiceUtils.transformFetchResponse)
            .then(result => zzipCache[cacheKey] = result.zzipRecords);
    }

    // wait for promise to resolve
    if (zzipCache[cacheKey] && zzipCache[cacheKey] instanceof Promise) {
        await zzipCache[cacheKey]
    }

    // return result
    return zzipCache[cacheKey];
}

/**
 * Builds a url based on if using aws service and the pathParam
 * and queryParams passed in.
 *
 * @param queryParams - will be added as params on the url. eid only
 *                      added here if not aws service.
 * @param pathParam - will be added to end of path.
 * @return {string}
 */
function getUrl(queryParams, pathParam = '') {
    let url;
    if (!awsUrl) {
        awsUrl = UrlService.getAwsUrl();
    }
    delete queryParams.eid;
    url = `${awsUrl}${AWS_SHIPPING_PATH}${pathParam}?${UrlService.toQueryString(queryParams)}`;
    return url
}

/**
 * Returns the headers object
 *
 * @param eid - added if using aws service
 * @return {*}
 */
function getHeaders(eid) {
    let retHeader;

    headers = headers || ServiceUtils.getHeaders();
    retHeader = Object.assign({Authorization: `JWT ${eid}`, 'content-type': 'application/json'}, headers);

    return retHeader;
}

/**
 *
 * @param {ValidationAddress} address
 * @returns {Promise<ValidationAddressResponse>}
 */
async function useAddressValidationService(address) {
    const country = RunConfigService.getRunConfig().country;
    const env = RunConfigService.getEnvironmentCode();
    const url = STREET_ADDRESS_VALIDATOR_URLS[env] + country;
    let response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'omit',
        body: JSON.stringify(address)
    });
    return ServiceUtils.transformFetchResponse(response);
}

/**
 * Address data matching a given postal code
 * @typedef PostalCodeLookupDTO
 * @property {string} postalcode - Postal code
 * @property {string} region - Region/State name
 * @property {string} regionDesc - Region description
 * @property {string} district - District/County name
 * @property {string} districtDesc - District description
 * @property {string} city - City name
 * @property {string} street - Street name
 * @property {string} building - Building number
 */

/**
 * @typedef ShipDaysResponse
 * @property {number} status
 * @property {{
 * blackoutDays: number[],
 * blackoutPeriod: number,
 * defaultShipDay: number
 * }} ShipDaysResponse
 */

/**
 * @typedef ShippingAddressListResponse
 * @property {number} status
 * @property {ShippingAddressDTO[]} ShippingAddressList
 */

/**
 * @typedef ShippingAddressResponse
 * @property {number} status
 * @property {ShippingAddressDTO} ShippingAddress
 */

/**
 * Object representing a shipping address saved to a user's profile.
 * @typedef ShippingAddressDTO
 * @property {number} [beShippingAddressId] ID of this address. This value is
 * added by the server and should not be altered by the client.
 * @property {string} addressType
 * @property {boolean} [defaultShipping] Flag indicating whether this is the
 * user's default shipping address.
 * @property {string} shippingAddressName
 * @property {string} shippingAddress1
 * @property {string} shippingAddress2
 * @property {string} shippingCity
 * @property {string} shippingCounty
 * @property {string} shippingState
 * @property {string} shippingPostalCode
 * @property {string} shippingCountry
 * @property {string} [shippingPhone]
 * @property {string} [shippingPhoneCountry]
 * @property {string} [shippingMobilePhone]
 * @property {string} [shippingMobilePhoneCountry]
 * @property {string} [shippingEmail]
 * @property {string} [shippingInstructions]
 * @property {string} [shippingAlias]
 */

/**
 * @typedef VNRegion
 * @property {string} value
 * @property {string} text
 * @property {VNDistrict[]} districts
 */

/**
 * @typedef VNDistrict
 * @property {string} text
 * @property {string[]} cities
 */


/**
 * @typedef ValidationAddress
 * @property {string} street
 * @property {string} street2
 * @property {string} city
 * @property {string} district
 * @property {string} region
 * @property {string} postalCode
 */

/**
 * @typedef ValidationAddressResponse
 * @property {{
 * addressPrecision: string,
 * maxAddressPrecision: string,
 * verificationStatus: string
 * }} analysis
 * @property {ValidationAddress[]} candidates
 * @property {ValidationAddress} inputs
 */